import type { ReactNode } from 'react';
import { Link } from '@tanstack/react-router';
import { OwnerAvatar } from './OwnerAvatar';

type CommandCenterTab =
  | 'now'
  | 'project'
  | 'leadership'
  | 'retrospectives'
  | 'effectiveness'
  | 'patterns';

interface CommandCenterSidebarProps {
  activeTab: CommandCenterTab;
}

/** Fixed left-rail width - pages offset their content by this on desktop. */
export const SIDEBAR_WIDTH_CLASS = 'md:ps-[232px]';

/** Quiet ambient vitals - demo copy, sets pressure without clutter. */
const VITALS = [
  { label: 'Engineering', tone: 'muted' as const },
  { label: '11 open gaps', tone: 'muted' as const },
  { label: '5 critical', tone: 'urgent' as const },
];

interface NavItem {
  tab: CommandCenterTab;
  to: string;
  label: string;
  icon: ReactNode;
  disabled?: boolean;
}

interface NavSection {
  /** Small uppercase label above the group; omit for the first, label-less group. */
  heading?: string;
  items: NavItem[];
}

/**
 * Two scopes of work: "Operate" is the manager's live surfaces (triage + the
 * projects they run), "Intelligence" is the look-back / value surfaces (the
 * executive impact view and the retrospectives library).
 */
const NAV_SECTIONS: NavSection[] = [
  {
    heading: 'Operate',
    items: [
      {
        tab: 'now',
        to: '/command-center',
        label: 'Now',
        icon: <path d="M13 2L3 14h7l-1 8 10-12h-7l1-8z" />,
      },
      {
        tab: 'project',
        to: '/project',
        label: 'Projects',
        icon: (
          <path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V7z" />
        ),
      },
    ],
  },
  {
    heading: 'Intelligence',
    items: [
      {
        tab: 'leadership',
        to: '/leadership',
        label: 'Leadership',
        icon: (
          <>
            <path d="M3 3v18h18" />
            <path d="M7 14l3.5-4 3 2.5L20 6" />
          </>
        ),
      },
      {
        tab: 'retrospectives',
        to: '/retrospectives',
        label: 'Retrospectives',
        icon: (
          <>
            <path d="M3 12a9 9 0 1 0 2.6-6.3L3 8" />
            <path d="M3 3v5h5" />
            <path d="M12 7.5V12l3 2" />
          </>
        ),
      },
      {
        tab: 'effectiveness',
        to: '/effectiveness',
        label: 'Effectiveness',
        icon: (
          <>
            <circle cx="12" cy="12" r="9" />
            <path d="M12 12l5-3" />
            <circle cx="12" cy="12" r="1.5" fill="currentColor" stroke="none" />
          </>
        ),
      },
      {
        tab: 'patterns',
        to: '/command-center',
        label: 'Patterns',
        disabled: true,
        icon: <path d="M4 6h16M4 12h16M4 18h10" />,
      },
    ],
  },
];

/** Flattened enabled items - the mobile bar shows these without sections. */
const MOBILE_NAV_ITEMS = NAV_SECTIONS.flatMap((section) =>
  section.items.filter((item) => !item.disabled)
);

function NavIcon({
  children,
  filled,
}: {
  children: ReactNode;
  filled?: boolean;
}) {
  return (
    <svg
      viewBox="0 0 24 24"
      className="h-4 w-4"
      fill={filled ? 'currentColor' : 'none'}
      stroke="currentColor"
      strokeWidth={filled ? 0 : 2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {children}
    </svg>
  );
}

/** Brand lockup, reused in both the rail and the mobile bar. */
function Brand() {
  return (
    <Link to="/command-center" className="flex items-center gap-2.5">
      <img src="/intera_logo_blue.svg" alt="Intera" className="h-6 w-auto" />
      <span className="text-base font-semibold tracking-tight text-[#0E1B3A]">
        Intera
      </span>
    </Link>
  );
}

/**
 * The manager command-center navigation - a fixed left rail on desktop, a slim
 * top bar on mobile. Lets a manager move between the "Now" triage view and the
 * project deep dives.
 */
export function CommandCenterSidebar({ activeTab }: CommandCenterSidebarProps) {
  return (
    <>
      {/* Mobile top bar */}
      <header className="sticky top-0 z-30 flex h-16 items-center justify-between border-b border-zinc-200 bg-white px-5 md:hidden">
        <Brand />
        <nav className="flex items-center gap-4">
          {MOBILE_NAV_ITEMS.map((item) => (
            <Link
              key={item.label}
              to={item.to}
              className={`text-sm transition-colors ${
                activeTab === item.tab
                  ? 'font-medium text-black'
                  : 'text-zinc-400'
              }`}
            >
              {item.label}
            </Link>
          ))}
          <OwnerAvatar owner={{ name: 'Alex Morgan', initials: 'AM' }} />
        </nav>
      </header>

      {/* Desktop left rail */}
      <aside className="fixed inset-y-0 start-0 z-30 hidden w-[232px] flex-col border-e border-zinc-200 bg-white px-5 py-6 shadow-[1px_0_0_0_rgba(24,24,27,0.02)] md:flex">
        <Brand />

        <nav className="mt-9 flex flex-col gap-6">
          {NAV_SECTIONS.map((section) => (
            <div
              key={section.heading ?? 'main'}
              className="flex flex-col gap-1"
            >
              {section.heading && (
                <span className="mb-1 px-3 text-[10px] font-bold uppercase tracking-[0.16em] text-zinc-300">
                  {section.heading}
                </span>
              )}
              {section.items.map((item) => {
                const isActive = activeTab === item.tab && !item.disabled;
                if (item.disabled) {
                  return (
                    <span
                      key={item.label}
                      title="Coming soon"
                      className="flex cursor-not-allowed items-center gap-3 rounded-xl px-3 py-2.5 text-sm text-zinc-300"
                    >
                      <NavIcon>{item.icon}</NavIcon>
                      {item.label}
                    </span>
                  );
                }
                return (
                  <Link
                    key={item.label}
                    to={item.to}
                    className={`flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm transition-colors ${
                      isActive
                        ? 'bg-[#00AEEF]/10 font-medium text-[#0E1B3A]'
                        : 'text-zinc-500 hover:bg-zinc-50 hover:text-zinc-800'
                    }`}
                  >
                    <span className={isActive ? 'text-[#00AEEF]' : ''}>
                      <NavIcon filled={item.tab === 'now'}>{item.icon}</NavIcon>
                    </span>
                    {item.label}
                  </Link>
                );
              })}
            </div>
          ))}
        </nav>

        {/* Vitals + avatar pinned to the bottom. */}
        <div className="mt-auto">
          <div className="mb-4 flex flex-col gap-2 rounded-2xl border border-zinc-100 bg-white px-4 py-3 shadow-sm">
            {VITALS.map((vital) => (
              <span
                key={vital.label}
                className="flex items-center gap-2 text-xs"
              >
                {vital.tone === 'urgent' && (
                  <span className="h-1.5 w-1.5 rounded-full bg-rose-500" />
                )}
                <span
                  className={
                    vital.tone === 'urgent'
                      ? 'font-medium text-rose-600'
                      : 'text-zinc-500'
                  }
                >
                  {vital.label}
                </span>
              </span>
            ))}
          </div>
          <div className="flex items-center gap-3 px-1">
            <OwnerAvatar owner={{ name: 'Alex Morgan', initials: 'AM' }} />
            <div className="flex flex-col">
              <span className="text-sm font-medium text-zinc-800">
                Alex Morgan
              </span>
              <span className="text-[11px] text-zinc-400">
                Engineering manager
              </span>
            </div>
          </div>
        </div>
      </aside>
    </>
  );
}
